/**
 * Streaming handlers — tool-block mutations that do NOT ride the delta buffer.
 *
 * AGENT_TOOL_RESULT, AGENT_TOOL_USE_START. Each writes a
 * tool block straight through `_mutateTasks`.
 *
 * The other three tool events stay in `streamingHandlers.ts` because they are
 * welded to that file's module-scoped rAF batch queue: AGENT_TOOL_USE drains
 * buffered text before it appends a block, and AGENT_TOOL_INPUT_DELTA /
 * AGENT_TOOL_PROGRESS push onto the queue. Splitting those would mean exporting
 * mutable module state, which is worse than the split is good.
 *
 * Registered by `registerStreamingHandlers`, which owns the teardown this
 * returns. `streamingHandlers.registrationTable.test.ts` pins both halves.
 */

import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import { useTaskStore } from "../taskStore";
import { useBrowserStore } from "../browserStore";
import {
  isBrowserToolName,
  openComputerOnBrowserActivity,
} from "./browserAutoOpen";
import { stampAgentToolSurface } from "./agentSurface";
import { isDesktopShell } from "@/lib/desktop-shell";
import { wakeEmbeddedBrowser } from "@/lib/embedded-browser";
import { ensureTaskTab } from "@/lib/task-tabs";
import type { ToolUseBlock, ToolSummary } from "../types";
import { findLanePending } from "../selectors";
import { finalizeThinkingDurations } from "../helpers/contentBlocks";
import type { ClientPayloadOf } from "../socketService";

const taskStore = useTaskStore;

export function registerStreamingToolHandlers(socket: Socket): () => void {
  // UNDECLARED by the wire contract (s2-07b finding): summary, resultDisplay — read here, absent from AgentToolResultPayloadSchema.
  const handleToolResult = (data: ClientPayloadOf<typeof EVENTS.AGENT_TOOL_RESULT> & {
    summary?: ToolSummary;
    resultDisplay?: ToolUseBlock["resultDisplay"];
  }) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    const applyToBlock = (tb: ToolUseBlock) => {
      // A HITL pause is a TYPED suspension, not a terminal result: fold
      // status:'paused' + isError:false (never an error chip) so the block itself
      // reads "awaiting" on reload, even when no live lane entry exists. New-sandbox
      // wire (paused:true); a non-paused result stays byte-identical.
      const paused = data.paused === true;
      tb.status = paused ? "paused" : data.isError ? "error" : "completed";
      tb.result =
        typeof data.content === "string"
          ? data.content
          : JSON.stringify(data.content);
      tb.isError = paused ? false : data.isError;
      tb.endTime = new Date();
      tb.resultSummary = data.summary;
      tb.resultDisplay = data.resultDisplay;
      // Resilience: if this is a subagent (Task/Agent tool) and SUBAGENT_END was missed,
      // auto-complete the subagent status so the card doesn't stay stuck at "running"
      if (tb.subagentStatus === "running") {
        tb.subagentStatus = paused
          ? "paused"
          : data.isError
            ? "error"
            : "completed";
      }
    };

    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;

      // T9/P2.4 — a subagent HITL pause surfaces the parent Agent tool_result as a
      // pause. New sandbox: a TYPED `paused:true` result (isError:false). Old
      // sandbox (mixed-version window): the isError:true PAUSE SENTINEL. Either
      // shape is a suspension, not a failure — while a live lane pends, skip the
      // fold entirely so the block stays 'running' and the pending lane
      // (pendingByLane) renders "needs you" instead of a terminal error the lane
      // can never recover from (the SUBAGENT_START monotonic guard refuses
      // error→running on resume). The REAL result folds on the post-approval
      // re-drive. Keyed STRICTLY on pendingByLane: a main-agent pause rides
      // `pendingInteraction`, so its handling stays byte-identical. With NO live
      // lane, `applyToBlock` still folds `paused:true` to status:'paused' (not an
      // error) so a reload reads the pause from the persisted block.
      if (
        (data.paused === true || data.isError) &&
        task.pendingByLane &&
        findLanePending(data.toolUseId, task.pendingByLane)
      ) {
        return;
      }

      // Try streaming message first (normal case)
      if (task.streamingMessage) {
        for (const block of task.streamingMessage.content) {
          if (
            block.type === "tool_use" &&
            (block as ToolUseBlock).toolUseId === data.toolUseId
          ) {
            applyToBlock(block as ToolUseBlock);
            return;
          }
        }
      }

      // Fallback: tool result arrived after message was committed (multi-turn race)
      for (const msg of task.messages) {
        if (msg.role !== "assistant") continue;
        for (const block of msg.content) {
          if (
            block.type === "tool_use" &&
            (block as ToolUseBlock).toolUseId === data.toolUseId
          ) {
            applyToBlock(block as ToolUseBlock);
            return;
          }
        }
      }
    });
  };

  const handleToolUseStart = (data: ClientPayloadOf<typeof EVENTS.AGENT_TOOL_USE_START>) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    // Desktop presence: the dock's live halo + menubar copy for shell and
    // file work. Flag-gated inside; a no-op for the split world.
    if (typeof data.toolName === "string")
      stampAgentToolSurface(taskId, data.toolName);

    // Agent-driven browser open: when the agent calls any browser-control
    // tool, auto-open the Computer panel on the Browser tab for this
    // task. Fires ONLY on LIVE tool invocations (AGENT_TOOL_USE_START is
    // emitted once per tool fire; it is NOT replayed by MESSAGE_SYNC on
    // reconnect). Putting this in the START handler instead of the
    // completed-TOOL_USE handler prevents the panel from spuriously
    // opening on every reconnect-replay.
    const isBrowserToolFire =
      typeof data.toolName === "string" && isBrowserToolName(data.toolName);
    // Embedded browser status-wake. The shell
    // lazy-inits CEF on its FIRST command and the local runner's attach
    // poll (10s) only succeeds once something woke it — but the lane only
    // reads live here (source==='embedded') AFTER BROWSER_LOCAL_READY,
    // which itself needs the attach. Waiting for READY would deadlock, so
    // browser activity pokes embedded_browser_status the moment it starts,
    // regardless of lane state. Fire-and-forget, deduped in the lib (one
    // invoke per few seconds), inert unless shell.
    if (isBrowserToolFire && isDesktopShell()) {
      wakeEmbeddedBrowser();
      // Real tabs per task: on a task handoff ask the SHELL for a fresh tab
      // (create) or bring the task's own tab forward (activate) — the runner
      // observes the target and binds the task to it. Tab mutations are
      // shell commands, never CDP (poison law); first task / cold pane is a
      // no-op (the runner claims the tab it is bound to).
      ensureTaskTab(taskId);
    }
    if (
      isBrowserToolFire &&
      (!isDesktopShell() ||
        useBrowserStore.getState().source === "local" ||
        useBrowserStore.getState().source === "embedded")
    ) {
      // In the browser webapp (not a shell), always open. In the desktop shell
      // the browser is LOCAL (LOCAL_BROWSER_V1) or EMBEDDED
      // (EMBEDDED_BROWSER_V1): open when either lane is live — it has an
      // in-app surface to show. The old `!isDesktopShell()`
      // blanket-suppressed the shell for the EXTENSION era (real Chrome, no
      // stream); the local browser reverses that. (This gate is re-asserted
      // here after a stale-base sibling reverted it — 2026-07-12.)
      //
      // browserStore.openPanelForTask tracks the BROWSER SESSION
      // lifecycle (which sandbox to keep awake) — unrelated to whether
      // the Computer panel is visible. Both need to fire.
      useBrowserStore.getState().openPanelForTask(taskId);
      // Remote lane only: the server's pane-demand
      // registry can only learn about this agent-opened pane from a
      // BROWSER_OPEN_SESSION — without it the machine keeps resolving `hidden`
      // and the snapshot never mounts pixels. Level-triggered guard: emit only
      // while the snapshot doesn't already show a demanded (non-hidden) pane,
      // so once the server answers, subsequent tool fires stay silent.
      if (!isDesktopShell()) {
        const snap = useBrowserStore.getState().viewSnapshot;
        if (!snap || snap.state === "hidden") {
          // origin:'agent' — this open is the AGENT's, relayed on the user's
          // behalf. It must register pane demand (the pane needs pixels) but
          // must NOT earn the server's keep-awake budget, which is a human's
          // ("left open 1 min"). Without the tag the server read the agent's
          // own browser use as the user touching the browser and held the
          // sandbox awake a full minute past the work, every time.
          socket.emit(EVENTS.BROWSER_OPEN_SESSION, { origin: "agent" });
        }
      }
      // computerStore.onAgentBrowserActivity is what actually opens the
      // Computer panel + snaps to the Browser tab, gated per world inside
      // openComputerOnBrowserActivity (legacy story-Window world skips;
      // everywhere else the panel IS the browser surface — founder 08-12:
      // "unless the user closes it, it should appear by default, always").
      openComputerOnBrowserActivity(taskId);
    }

    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task?.streamingMessage) return;

      // Don't create preparing block if full AGENT_TOOL_USE already arrived (fast tools)
      const existing = task.streamingMessage.content.find(
        (b) =>
          b.type === "tool_use" &&
          (b as ToolUseBlock).toolUseId === data.toolUseId
      );
      if (existing) return;

      finalizeThinkingDurations(task.streamingMessage.content);
      task.streamingMessage.content.push({
        type: "tool_use",
        toolUseId: data.toolUseId,
        toolName: data.toolName,
        toolInput: {},
        status: "preparing",
        startTime: new Date(),
      } as ToolUseBlock);
    });
  };

  socket.on(EVENTS.AGENT_TOOL_RESULT, handleToolResult);
  socket.on(EVENTS.AGENT_TOOL_USE_START, handleToolUseStart);

  return () => {
    socket.off(EVENTS.AGENT_TOOL_RESULT, handleToolResult);
    socket.off(EVENTS.AGENT_TOOL_USE_START, handleToolUseStart);
  };
}
